import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'

import { PanelContainer } from '../../../../../components/Panel'
import { usePanelContext } from '../../../../../contexts'
import { FormFieldName, FormValues } from './EInvoice.data'

type Props = {
  formik: FormikProps<FormValues>
  isRequestRunning: boolean
}

export const EInvoiceView: React.FC<Props> = ({ formik, isRequestRunning }) => {
  const { t, i18n } = useTranslation()
  const { isEdit } = usePanelContext()

  const panelId = 'settings-e-invoice'

  const xIncVersions = [
    { label: '3.0.2', value: '3.0.2' },
    { label: '3.0.1', value: '3.0.1' },
    { label: '3.0.0', value: '3.0.0' },
  ]

  const zugfVersions = [
    { label: '2.3.3', value: '2.3.3' },
    { label: '2.3.2', value: '2.3.2' },
    { label: '2.3.1', value: '2.3.1' },
    { label: '2.2', value: '2.2' },
    { label: '2.1.1', value: '2.1.1' },
    { label: '2.0', value: '2.0' },
  ]

  const options = [
    {
      label: t(
        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.versionComparer.options.exact',
      ),
      value: 0,
    },
    {
      label: t(
        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.versionComparer.options.minimum',
      ),
      value: 1,
    },
  ]

  const isFormFieldInvalid = (name: FormFieldName) => !!formik.errors[name]

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : null
  }

  return (
    <PanelContainer
      header={t('buyerSettings.eInvoice.title')}
      formik={formik}
      id={panelId}
      toggleable={false}
      isRequestRunning={isRequestRunning}
    >
      <div className="grid">
        {/* E-Invoice max size */}
        <div className="col-12 lg:col-4">
          <h3 className="m-0">
            {t('buyerSettings.eInvoice.eInvoiceSize.title')}
          </h3>
          <p>{t('buyerSettings.eInvoice.eInvoiceSize.description')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <div className="flex flex-column gap-1">
              <label className="text-xs font-bold" htmlFor="xIncActive">
                {t(
                  'buyerSettings.eInvoice.eInvoiceSize.form.maxFileSize.label',
                )}
              </label>
              <InputNumber
                value={formik.values.maxFileSize}
                onValueChange={e =>
                  formik.setFieldValue('maxFileSize', e.value)
                }
                min={0}
                max={1000}
                placeholder={t(
                  'buyerSettings.eInvoice.eInvoiceSize.form.maxFileSize.placeholder',
                )}
                showButtons
                locale={i18n.language}
                suffix={t(
                  'buyerSettings.eInvoice.eInvoiceSize.form.maxFileSize.suffix',
                )}
                invalid={isFormFieldInvalid('maxFileSize')}
                disabled={!isEdit(panelId)}
                className="w-full md:w-14rem"
              />
              {getFormErrorMessage('maxFileSize')}
            </div>
          </Card>
        </div>

        <div className="col-12">
          <Divider />
        </div>

        {/* E-Invoice format */}
        <div className="col-12 lg:col-4">
          <h3 className="m-0">
            {t('buyerSettings.eInvoice.eInvoiceFormat.title')}
          </h3>
          <p>{t('buyerSettings.eInvoice.eInvoiceFormat.description')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            {/* XRechnung */}
            <div className="flex flex-column md:flex-row flex-wrap md:align-items-center justify-content-between gap-4">
              <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="xIncActive"
                  checked={formik.values.xIncActive}
                  onChange={formik.handleChange}
                  disabled={!isEdit(panelId)}
                />
                <label className="font-bold	text-lg" htmlFor="xIncActive">
                  {t(
                    'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.title',
                  )}
                </label>
                {getFormErrorMessage('xIncActive')}
              </div>

              {formik.values.xIncActive && (
                <div className="flex flex-column sm:flex-row gap-4">
                  <div className="flex flex-column w-full">
                    <label className="hidden" htmlFor="xIncVersionComparer">
                      {t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.versionComparer.label',
                      )}
                    </label>
                    <Dropdown
                      id="xIncVersionComparer"
                      value={formik.values.xIncVersionComparer}
                      onChange={formik.handleChange}
                      optionLabel="label"
                      optionValue="value"
                      options={options}
                      placeholder={t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.versionComparer.placeholder',
                      )}
                      className="w-full md:w-10rem"
                      invalid={isFormFieldInvalid('xIncVersionComparer')}
                      disabled={!isEdit(panelId)}
                    />
                    {getFormErrorMessage('xIncVersionComparer')}
                  </div>
                  <div className="flex flex-column w-full">
                    <label className="hidden" htmlFor="xIncVersion">
                      {t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.version.label',
                      )}
                    </label>
                    <Dropdown
                      id="xIncVersion"
                      value={formik.values.xIncVersion}
                      onChange={formik.handleChange}
                      optionLabel="label"
                      optionValue="value"
                      options={xIncVersions}
                      placeholder={t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.xInvoice.version.placeholder',
                      )}
                      className="w-full md:w-7rem"
                      invalid={isFormFieldInvalid('xIncVersion')}
                      disabled={!isEdit(panelId)}
                    />
                    {getFormErrorMessage('xIncVersion')}
                  </div>
                </div>
              )}
            </div>
            <Divider />
            {/* ZUGFeRD */}
            <div className="flex flex-column md:flex-row flex-wrap md:align-items-center justify-content-between gap-4">
              <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="zugfActive"
                  checked={formik.values.zugfActive}
                  onChange={formik.handleChange}
                  disabled={!isEdit(panelId)}
                />
                <label className="font-bold	text-lg" htmlFor="zugfActive">
                  {t(
                    'buyerSettings.eInvoice.eInvoiceFormat.form.zugferd.title',
                  )}
                </label>
                {getFormErrorMessage('zugfActive')}
              </div>

              {formik.values.zugfActive && (
                <div className="flex flex-column sm:flex-row gap-4">
                  <div className="flex flex-column w-full">
                    <label className="hidden" htmlFor="zugfVersionComparer">
                      {t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.zugferd.versionComparer.label',
                      )}
                    </label>
                    <Dropdown
                      id="zugfVersionComparer"
                      value={formik.values.zugfVersionComparer}
                      onChange={formik.handleChange}
                      optionLabel="label"
                      optionValue="value"
                      options={options}
                      placeholder={t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.zugferd.versionComparer.placeholder',
                      )}
                      className="w-full md:w-10rem"
                      invalid={isFormFieldInvalid('zugfVersionComparer')}
                      disabled={!isEdit(panelId)}
                    />
                    {getFormErrorMessage('zugfVersionComparer')}
                  </div>
                  <div className="flex flex-column w-full">
                    <label className="hidden" htmlFor="zugfVersion">
                      {t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.zugferd.version.label',
                      )}
                    </label>
                    <Dropdown
                      id="zugfVersion"
                      value={formik.values.zugfVersion}
                      onChange={formik.handleChange}
                      optionLabel="label"
                      optionValue="value"
                      options={zugfVersions}
                      placeholder={t(
                        'buyerSettings.eInvoice.eInvoiceFormat.form.zugferd.version.placeholder',
                      )}
                      className="w-full md:w-7rem"
                      invalid={isFormFieldInvalid('zugfVersion')}
                      disabled={!isEdit(panelId)}
                    />
                    {getFormErrorMessage('zugfVersion')}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* TODO: Activate code when it is implemented in backend */}
        {/* <div className="col-12">
          <Divider />
        </div> */}

        {/* E-Invoice functions */}
        {/* <div className="col-12 lg:col-4">
          <h3 className="m-0">
            {t('buyerSettings.eInvoice.eInvoiceFunctions.title')}
          </h3>
          <p>{t('buyerSettings.eInvoice.eInvoiceFunctions.description')}</p>
        </div> */}
        {/* <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4"> */}
        {/* Additional data */}
        {/* <div className="flex flex-column gap-1">
              <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="xIncActive"
                  className="flex-shrink-0"
                  checked={formik.values.xIncActive}
                  onChange={formik.handleChange}
                  disabled // {!isEdit(panelId)}
                />
                <div>
                  <label className="font-bold	text-lg" htmlFor="xIncActive">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.additionalData.title',
                    )}
                  </label>
                  <p className="text-sm m-0 mt-1">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.additionalData.description',
                    )}
                  </p>
                </div>
              </div>

              <Divider /> */}

        {/* Additional documents */}
        {/* <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="xIncActive"
                  className="flex-shrink-0"
                  checked={formik.values.xIncActive}
                  onChange={formik.handleChange}
                  disabled // {!isEdit(panelId)}
                />
                <div>
                  <label className="font-bold	text-lg" htmlFor="xIncActive">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.additionalDocuments.title',
                    )}
                  </label>
                  <p className="text-sm m-0 mt-1">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.additionalDocuments.description',
                    )}
                  </p>
                </div>
              </div>

              <Divider /> */}

        {/* Invoice validation */}
        {/* <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="xIncActive"
                  className="flex-shrink-0"
                  checked={formik.values.xIncActive}
                  onChange={formik.handleChange}
                  disabled // {!isEdit(panelId)}
                />
                <div>
                  <label className="font-bold	text-lg" htmlFor="xIncActive">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.invoiceCheck.title',
                    )}
                  </label>
                  <p className="text-sm m-0 mt-1">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.invoiceCheck.description',
                    )}
                  </p>
                </div>
              </div>

              <Divider /> */}

        {/* Virus protection */}
        {/* <div className="flex flex-row align-items-center gap-4">
                <InputSwitch
                  id="xIncActive"
                  className="flex-shrink-0"
                  checked={formik.values.xIncActive}
                  onChange={formik.handleChange}
                  disabled // {!isEdit(panelId)}
                />
                <div>
                  <label className="font-bold	text-lg" htmlFor="xIncActive">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.virusProtection.title',
                    )}
                  </label>
                  <p className="text-sm m-0 mt-1">
                    {t(
                      'buyerSettings.eInvoice.eInvoiceFunctions.form.virusProtection.description',
                    )}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div> */}
      </div>
    </PanelContainer>
  )
}
